import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Wand2 } from 'lucide-react';

import unpaidCard1x from '../../../application/assets/images/unpaid_card.png';
import unpaidCard2x from '../../../application/assets/images/unpaid_card@2x.png';
import unpaidCard3x from '../../../application/assets/images/unpaid_card@3x.png';

import { PixelTextSolid } from '@/components/PixelText';
import { AppleMark } from '@/components/icons/AppleMark';
import { APP_STORE_URL, LINKS } from '@/lib/constants';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

const platformCards = [
  { name: 'iOS', status: 'Available now' },
  { name: 'Web', status: 'In progress' },
  { name: 'Wearables', status: 'Exploring Meta Glasses' },
];

const imaginerSteps = [
  {
    title: 'Describe the outcome',
    description: 'Use natural language to express intent, not implementation.',
  },
  {
    title: 'Iterate with taste',
    description: 'Refine UI, flows, and behavior with fast feedback.',
  },
  {
    title: 'Ship real software',
    description: 'Build things people can use—quickly, elegantly.',
  },
];

export function HeroSection() {
  return (
    <section className="min-h-screen pt-32 pb-20 px-5 lg:px-12 bg-black">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Column */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col"
          >
            {/* Badge */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.08] border border-white/[0.15] text-xs font-medium text-white/80">
                <Sparkles className="w-3.5 h-3.5" />
                Now available on iOS · More platforms coming
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="mt-8 text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.1] tracking-[-0.02em] text-white"
            >
              Sculpt your idea.
              <br />
              <br />
              <PixelTextSolid className="text-[1.2em] sm:text-[1.25em] block mt-1">
                Michelangelo
              </PixelTextSolid>
              <span className="text-white/80">will do the rest.</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="mt-6 text-base lg:text-lg text-white/60 leading-relaxed max-w-xl"
            >
              Michelangelo.land is more than an AI app builder: it&apos;s an ecosystem where
              artificial intelligence meets human creativity—turning ideas into real applications
              with speed and precision, without compromise.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap gap-3">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 rounded-lg text-sm font-medium hover:bg-white/90 transition-all duration-200 hover:scale-[1.02]"
              >
                <AppleMark className="w-4 h-4" />
                <span>Download for iOS</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={LINKS.discord}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium text-white border border-white/20 hover:bg-white/5 transition-all duration-200"
              >
                Join the community
              </a>
              <a
                href="#manifesto"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              >
                Read the manifesto
              </a>
            </motion.div>

            {/* Platform Cards */}
            <motion.div variants={itemVariants} className="mt-10 grid grid-cols-3 gap-3">
              {platformCards.map((platform) => (
                <div key={platform.name} className="glass-card glass-card-hover p-4">
                  <p className="text-sm font-semibold text-white">{platform.name}</p>
                  <p className="text-xs text-white/50 mt-1">{platform.status}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="flex flex-col gap-4"
          >
            {/* Feature Card */}
            <div className="glass-card glass-card-hover p-6">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="w-5 h-5 text-white/70" />
                <h3 className="text-base font-semibold text-white">The Imaginer&apos;s loop</h3>
              </div>
              <p className="text-sm text-white/50 mb-4">
                From thought to product, without losing momentum.
              </p>
              <div className="space-y-3">
                {imaginerSteps.map((step, index) => (
                  <div key={step.title} className="flex gap-3">
                    <div
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{
                        background: index === 0 ? '#6b8cff' : index === 1 ? '#a855f7' : '#22d3ee',
                      }}
                    />
                    <div>
                      <p className="text-sm font-medium text-white/80">{step.title}</p>
                      <p className="text-xs text-white/50">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative rounded-xl overflow-hidden border border-white/[0.08]">
              <img
                src={unpaidCard3x}
                srcSet={`${unpaidCard1x} 274w, ${unpaidCard2x} 548w, ${unpaidCard3x} 822w`}
                sizes="(min-width: 1024px) 420px, 100vw"
                alt="Michelangelo app visual"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
